/**
 * <aspilon-checkout-button> — add the selected variant to the cart, then go
 * straight to checkout.
 *
 * Uses the product form in the same section (Horizon's <product-form-component>),
 * so variant and quantity are whatever the customer has selected. Availability
 * mirrors the Add-to-cart button: when that one is disabled, so is this.
 */
class AspilonCheckoutButton extends HTMLElement {
  #busy = false;
  #observer = null;

  connectedCallback() {
    this.button = this.querySelector('.aspilon-checkout__button');
    this.error = this.querySelector('.aspilon-checkout__error');
    if (!this.button) return;

    this.form = this.#findForm();
    this.addToCart = this.form?.closest('product-form-component')?.querySelector('add-to-cart-component button') || null;

    if (!this.form) {
      this.button.disabled = true;
      return;
    }

    this.#syncDisabled();
    if (this.addToCart) {
      this.#observer = new MutationObserver(() => this.#syncDisabled());
      this.#observer.observe(this.addToCart, { attributes: true, attributeFilter: ['disabled'] });
    }

    this.button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.button?.removeEventListener('click', this.#onClick);
  }

  #findForm() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  }

  #syncDisabled() {
    if (this.#busy) return;
    this.button.disabled = !!this.addToCart?.disabled;
  }

  #setLabel(text) {
    this.querySelectorAll('.button-motion__label').forEach((el) => (el.textContent = text));
  }

  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.#busy || this.button.disabled) return;

    // Re-resolve in case the section re-rendered on a variant change.
    this.form = this.#findForm() || this.form;
    if (!this.form) return;

    this.#busy = true;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    if (this.error) this.error.hidden = true;
    this.#setLabel(this.dataset.loadingLabel || 'One moment…');

    try {
      const formData = new FormData(this.form);
      if (!formData.get('quantity')) formData.set('quantity', '1');
      formData.delete('sections');

      const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || this.dataset.errorLabel);
      }

      window.location.assign('/checkout');
    } catch (err) {
      this.#showError((err && err.message) || this.dataset.errorLabel || 'Something went wrong.');
      this.#setLabel(this.button.dataset.label || 'Checkout');
      this.button.removeAttribute('aria-busy');
      this.#busy = false;
      this.#syncDisabled();
    }
  };
}

if (!customElements.get('aspilon-checkout-button')) {
  customElements.define('aspilon-checkout-button', AspilonCheckoutButton);
}
